import { useState } from "react";
import ReactPaginate from "react-paginate";

import { ResumeModal } from "./resume-modal";
import { NotificationModal } from "../common/notification-modal";
import { LoadingSpinner, LoadingSpinnerForTable } from "../common/loading-spinner";
import { EnrolleeData, ErrorType } from "../../models";
import { StatusChangePrompt } from "./status-change-prompt";
import { AlumniModal } from "./alumni-modal";
import { useChangeEnrolleeStatusMutation } from "../../store/rtk/admin-api";

interface AdminPendingEnrolleesTableProps {
  pendingEnrollees: EnrolleeData[];
  setEnrollees: React.Dispatch<React.SetStateAction<EnrolleeData[]>>;
  modalDataUseState: [string, React.Dispatch<React.SetStateAction<string>>];
  alumni: EnrolleeData[];
  isLoading: boolean;
}

export const AdminPendingEnrolleesTable = ({
  pendingEnrollees,
  setEnrollees,
  modalDataUseState,
  alumni,
  isLoading,
}: AdminPendingEnrolleesTableProps) => {
  const [itemOffset, setItemOffset] = useState(0);
  const [modalData, setModalData] = modalDataUseState;
  const [selectedEnrollee, setSelectedEnrollee] = useState<EnrolleeData | null>();
  const [selectedAction, setSelectedAction] = useState<"approve" | "reject" | "">("");

  const [showErrorBanner, setShowErrorBanner] = useState(false);
  const [serverError, setServerError] = useState<null | string>(null);
  const [changeEnrolleeStatus, { isLoading: isChangingEnrolleeStatus }] = useChangeEnrolleeStatusMutation();
  const itemsPerPage = 5;

  function EnrolleeRow({ pendEnrollees }: { pendEnrollees: EnrolleeData[] }) {
    return (
      <>
        {pendEnrollees &&
          pendEnrollees!.map((enrollee, index) => (
            <tr key={enrollee.id}>
              <td className="py-1">{pendingEnrollees.findIndex(user => user === enrollee) + 1}</td>
              <td>{`${enrollee.fName} ${enrollee.lName}`}</td>
              <td>{enrollee.email}</td>
              <td>{enrollee.phoneNo}</td>
              <td>
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  data-bs-toggle="modal"
                  data-bs-target="#resumeModal"
                  onClick={() => setModalData(enrollee.resumeUrl)}
                >
                  View
                </button>
              </td>
              <td>
                <button
                  type="button"
                  data-bs-toggle="modal"
                  data-bs-target="#confirmModal"
                  onClick={() => {
                    setSelectedEnrollee(enrollee);
                    setSelectedAction("approve");
                    // changeEnrolleeStatus(enrollee.id, "approve");
                    // setshowConfirmationPrompt(true);
                  }}
                  className="btn btn-success btn-sm"
                >
                  Yes
                </button>
              </td>
              <td>
                <button
                  type="button"
                  data-bs-toggle="modal"
                  data-bs-target="#confirmModal"
                  className="btn btn-danger btn-sm"
                  onClick={() => {
                    setSelectedEnrollee(enrollee);
                    setSelectedAction("reject");
                    // changeEnrolleeStatus(enrollee.id, "reject");
                    // setshowConfirmationPrompt(true);
                  }}
                >
                  No
                </button>
              </td>
            </tr>
          ))}
      </>
    );
  }

  const endOffset = itemOffset + itemsPerPage;
  const currentEnrollees = pendingEnrollees.slice(itemOffset, endOffset);
  const pageCount = Math.ceil(pendingEnrollees.length / itemsPerPage);

  // Invoke when user click to request another page.
  const handlePageClick = (event: { selected: number }) => {
    const newOffset = (event.selected * itemsPerPage) % pendingEnrollees.length;
    setItemOffset(newOffset);
  };

  const changeEnrolleeStatusFn = async (enrolleeId: number, action: string) => {
    const status = action === "approve" ? "approved" : "rejected";

    const sendDataToServer = async () => {
      const serverRes = (await changeEnrolleeStatus({ enrollmentId: enrolleeId, status })) as {
        data: string;
        error: ErrorType;
      };
      if (serverRes.error) {
        setServerError(serverRes.error.data.error.message);
        setShowErrorBanner(true);
        return;
      }
    };

    sendDataToServer();
  };

  return (
    <>
      {isChangingEnrolleeStatus && <LoadingSpinner />}
      {serverError && showErrorBanner && (
        <NotificationModal
          onClose={() => {
            setShowErrorBanner(false);
            setServerError(null);
          }}
          message={serverError as string}
          show={true}
          success={false}
        />
      )}
      <div className="container-fluid " style={{ marginTop: 35 }}>
        <div className="row">
          <div className="col-lg-12 grid-margin stretch-card">
            <div className="card shadow" style={{ border: "1px solid #ffc107" }}>
              <div className="card-header" style={{ backgroundColor: "#FEDC79" }}>
                <div className="d-flex justify-content-between">
                  <h4>Pending Enrollees</h4>
                  <button
                    type="button"
                    className="btn btn-outline-primary py-0"
                    data-bs-toggle="modal"
                    data-bs-target="#viewAlumni"
                  >
                    View alumni
                  </button>
                </div>
              </div>
              <div className="card-body">
                <div className="table-responsive" style={{ minHeight: "15rem" }}>
                  <table className="table table-hover" style={{ tableLayout: "auto" }}>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Name</th>
                        <th>email</th>
                        <th>Phone No.</th>
                        <th>Resume</th>
                        <th>
                          <i className="fas fa-check"></i>
                        </th>
                        <th>
                          <i className="fas fa-times"></i>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <EnrolleeRow pendEnrollees={currentEnrollees} />
                      {isLoading && <LoadingSpinnerForTable />}
                      {!isLoading && pendingEnrollees.length === 0 && (
                        <tr
                          style={{
                            backgroundColor: "#fff",
                            height: "100%",
                            border: "none",
                            borderColor: "transparent",
                            fontSize: "1.2rem",
                          }}
                        >
                          <td colSpan={100} style={{ textAlign: "center", backgroundColor: "#fff" }}>
                            No pending enrollees found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="row">
                  <nav aria-label="Page navigation example">
                    <ul className="pagination justify-content-end">
                      <ReactPaginate
                        nextLabel="next >"
                        onPageChange={handlePageClick}
                        pageRangeDisplayed={3}
                        marginPagesDisplayed={2}
                        pageCount={pageCount}
                        previousLabel="< previous"
                        pageClassName="page-item"
                        pageLinkClassName="page-link"
                        previousClassName="page-item"
                        previousLinkClassName="page-link"
                        nextClassName="page-item"
                        nextLinkClassName="page-link"
                        breakLabel="..."
                        breakClassName="page-item"
                        breakLinkClassName="page-link"
                        containerClassName="pagination"
                        activeClassName="active"
                        renderOnZeroPageCount={null}
                      />
                    </ul>
                  </nav>
                </div>
              </div>
              <ResumeModal resumePath={modalData} />
              <AlumniModal alumni={alumni} isLoading={isLoading} />
              <StatusChangePrompt
                enrolleeName={`${selectedEnrollee?.fName} ${selectedEnrollee?.lName}`}
                enrolleeId={selectedEnrollee?.id as number}
                status={selectedAction}
                changeEnrolleeStatusFunc={changeEnrolleeStatusFn}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
